import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  School, CheckCircle2, ArrowRight, Sparkles, Calendar,
  BarChart3, Users, ShieldCheck, CreditCard, BookOpen, Clock, Layers,
  ChevronRight, ArrowUpRight, Server, Lock, UserCheck, BellRing
} from "lucide-react";
import { Reveal, SectionLabel, Stagger, StaggerItem, Highlight } from "@/components/motion";
import CTABanner from "@/components/CTABanner";

const MODULES = [
  {
    icon: UserCheck,
    title: "Admissions & Student Information System (SIS)",
    desc: "Complete digital student lifecycle from online application and document verification to alumni records and transcript generation.",
    points: ["Online paperless admissions", "Student 360° health & academic file", "Guardian & sibling linking", "Custom student ID card generation"],
  },
  {
    icon: CreditCard,
    title: "Automated Fee Collection & Financials",
    desc: "Automate tuition fee schedules, installment tracking, automated SMS/WhatsApp reminders, and online payment gateway processing.",
    points: ["Automated invoice & receipt generation", "Late fee rules & discount waivers", "Online parent payment portal", "Real-time daily collection ledger"],
  },
  {
    icon: Clock,
    title: "Live Attendance & Biometric Integration",
    desc: "Capture daily classroom attendance via mobile app or automated RFID/biometric gates with instant absent notifications sent to parents.",
    points: ["One-tap teacher mobile attendance", "RFID gate integration ready", "Instant SMS/app absence alerts", "Monthly attendance rate reports"],
  },
  {
    icon: Calendar,
    title: "Timetable, Exam & Gradebook Engine",
    desc: "Conflict-free class schedule matrix generator and comprehensive grading engine supporting GPA, percentage, and custom credit schemes.",
    points: ["Smart conflict-free timetable builder", "Exam schedule & seating planner", "Automated report card generation", "Teacher substitution manager"],
  },
];

const ROLES = [
  {
    title: "Principals & Headmasters",
    desc: "Real-time institutional oversight across enrollment numbers, teacher workloads, fee recovery, and campus performance indicators.",
    metrics: "360° total visibility across all campus branches",
  },
  {
    title: "Teachers & Academic Staff",
    desc: "Take attendance in seconds, enter grades from anywhere, schedule homework, and send announcements directly to student groups.",
    metrics: "12 hours saved weekly on administrative paperwork",
  },
  {
    title: "Parents & Guardians",
    desc: "Dedicated mobile portal for tracking child's attendance, reviewing report cards, paying fees online, and messaging teachers.",
    metrics: "96% on-time fee collection rate",
  },
];

const TECH_SPECS = [
  { icon: Server, label: "Campus Scalability", val: "Single School to Multi-Branch Networks" },
  { icon: Lock, label: "Student Data Privacy", val: "FERPA & GDPR Compliant Architecture" },
  { icon: Layers, label: "Hardware Support", val: "RFID · Biometric Gates · GPS Bus Tracking" },
  { icon: Clock, label: "System Availability", val: "99.99% Cloud SLA with Daily Backups" },
];

export default function ProductSchool() {
  return (
    <div data-testid="product-school-page">
      {/* HERO */}
      <section className="relative pt-36 pb-20 px-5 sm:px-8 overflow-hidden bg-white" data-testid="school-hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#CAF2F9]/30 to-white pointer-events-none" />
        <div className="absolute -top-24 right-1/3 w-[500px] h-[500px] rounded-full bg-[#09B2ED]/15 blur-3xl pointer-events-none" />
        <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <Reveal>
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="font-mono-brand inline-flex items-center gap-2 bg-[#CAF2F9]/60 border border-[#91E0F1]/50 text-[#2563EB] text-[11px] font-semibold tracking-[0.16em] uppercase px-3.5 py-1.5 rounded-full">
                  <School size={14} className="text-[#09B2ED]" /> Complete Campus Operating System
                </span>
              </div>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#01193D] leading-[1.1]">
                The Aurge Labs - <span className="text-gradient">School Management System</span>
              </h1>
              <p className="font-display font-medium text-xl sm:text-2xl text-[#2563EB] mt-4">
                Your entire school community — connected, automated, and paperless.
              </p>
              <p className="text-[#475569] text-base lg:text-lg leading-relaxed mt-4 max-w-2xl mx-auto">
                From kindergarten to university campuses, replace fragmented spreadsheets with one connected platform for admissions, fee collection, attendance, grading, and parent portals.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
                <Link
                  to="/contact"
                  data-testid="school-hero-demo-cta"
                  className="btn-gradient text-white text-sm font-semibold px-8 py-3.5 rounded-full inline-flex items-center gap-2"
                >
                  Schedule Live School Demo <ArrowRight size={16} />
                </Link>
                <a
                  href="#modules"
                  data-testid="school-hero-explore-cta"
                  className="btn-outline-navy text-sm font-semibold px-7 py-3.5 rounded-full inline-flex items-center gap-1.5"
                >
                  Explore Modules <ChevronRight size={15} />
                </a>
              </div>
            </Reveal>
          </div>

          {/* Interactive School SIS UI Preview */}
          <Reveal delay={0.25} className="mt-14 max-w-5xl mx-auto">
            <div className="rounded-3xl bg-white soft-shadow border border-slate-200/90 overflow-hidden shadow-2xl">
              {/* Top Bar */}
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 bg-[#F8FAFC]">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-rose-400/80" />
                  <span className="w-3 h-3 rounded-full bg-amber-400/80" />
                  <span className="w-3 h-3 rounded-full bg-emerald-400/80" />
                  <div className="ml-3 h-6 px-3 rounded-md bg-white border border-slate-200 flex items-center">
                    <span className="font-mono text-[10px] text-slate-500">app.school.theaurgelabs.com/campus/oakridge-academy</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="font-mono text-[10px] text-emerald-600 font-semibold">Campus Active · Term 2</span>
                </div>
              </div>

              {/* SIS Canvas */}
              <div className="p-6 sm:p-8 bg-slate-50/50">
                {/* Metric Summary Ribbon */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: "Enrolled Students", val: "1,240", change: "98.2% present today", color: "#2563EB" },
                    { label: "Term Fee Collected", val: "96.4%", change: "$420k collected", color: "#09B2ED" },
                    { label: "Faculty & Staff", val: "84", change: "100% active schedules", color: "#2563EB" },
                    { label: "Parent App Adoption", val: "94.8%", change: "1,180 active guardians", color: "#09B2ED" },
                  ].map((stat, i) => (
                    <div key={i} className="bg-white rounded-2xl p-4 border border-slate-100 soft-shadow">
                      <div className="font-mono text-[10px] uppercase text-slate-400 font-semibold">{stat.label}</div>
                      <div className="font-display font-bold text-2xl text-[#01193D] mt-1">{stat.val}</div>
                      <div className="font-mono text-[10px] text-emerald-600 font-medium mt-1">{stat.change}</div>
                    </div>
                  ))}
                </div>

                {/* Live Campus Telemetry Grid */}
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Left: Class Attendance Matrix */}
                  <div className="md:col-span-2 bg-white rounded-2xl border border-slate-100 p-5 soft-shadow">
                    <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                      <div>
                        <h4 className="font-display font-semibold text-sm text-[#01193D]">Today's Grade Attendance & Fee Pacing</h4>
                        <p className="text-[11px] text-slate-400 font-mono">Academic Year 2026–2027</p>
                      </div>
                      <span className="font-mono text-xs font-semibold bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full">
                        Gate RFID Synced
                      </span>
                    </div>

                    <div className="space-y-3 mt-4">
                      {[
                        { grade: "Grade 10 — Advanced Science", present: "34 / 35", feePct: 98, status: "Normal" },
                        { grade: "Grade 11 — Mathematics & Computing", present: "29 / 30", feePct: 95, status: "Normal" },
                        { grade: "Grade 12 — Senior Leadership", present: "32 / 32", feePct: 100, status: "100% Present" },
                      ].map((row, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
                          <div>
                            <div className="text-xs font-bold text-[#01193D]">{row.grade}</div>
                            <div className="text-[10px] text-slate-500 font-mono mt-0.5">Attendance: <span className="font-bold text-emerald-600">{row.present}</span></div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs font-bold text-[#2563EB]">{row.feePct}% Fees Cleared</div>
                            <div className="w-24 bg-slate-200 h-1.5 rounded-full overflow-hidden mt-1">
                              <div className="h-full bg-[#09B2ED] rounded-full" style={{ width: `${row.feePct}%` }} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right: Parent Portal Notification Feed */}
                  <div className="bg-[#01193D] text-white rounded-2xl p-5 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[10px] text-[#91E0F1] uppercase tracking-wider font-semibold">Parent Broadcast Hub</span>
                        <BellRing size={15} className="text-[#09B2ED]" />
                      </div>
                      <h5 className="font-display font-semibold text-sm mt-3">Automated Alerts Feed</h5>
                      <div className="space-y-2.5 mt-3 text-xs">
                        <div className="p-2.5 rounded-xl bg-white/10 text-slate-200">
                          <span className="text-[10px] font-mono text-[#09B2ED] block">Fee Receipt #8921</span>
                          Instant payment receipt sent to 42 parents via SMS.
                        </div>
                        <div className="p-2.5 rounded-xl bg-white/10 text-slate-200">
                          <span className="text-[10px] font-mono text-emerald-400 block">Exam Results Published</span>
                          Term 1 report cards available for download in portal.
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-xs">
                      <span className="text-slate-400 font-mono text-[10px]">Zero Paperwork</span>
                      <span className="text-[#09B2ED] font-semibold">View Broadcasts →</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CORE MODULES */}
      <section id="modules" className="px-5 sm:px-8 py-20 lg:py-28 bg-[#F8FAFC] scroll-mt-24" data-testid="school-modules-section">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-3xl">
            <SectionLabel>Core Capabilities</SectionLabel>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[42px] tracking-tight text-[#01193D] leading-tight">
              Everything required to run a high-performing campus
            </h2>
            <p className="text-[#475569] text-base lg:text-lg mt-4 leading-relaxed">
              Engineered to replace manual paperwork with automated workflows for administrators, teachers, accountants, and parents.
            </p>
          </Reveal>

          <Stagger className="grid md:grid-cols-2 gap-6 mt-14">
            {MODULES.map((m) => (
              <StaggerItem key={m.title} className="card-lift bg-white rounded-3xl p-8 border border-slate-100 soft-shadow">
                <span className="w-12 h-12 rounded-2xl bg-[#CAF2F9]/60 border border-[#91E0F1]/40 flex items-center justify-center text-[#2563EB] mb-6">
                  <m.icon size={24} />
                </span>
                <h3 className="font-display font-bold text-xl text-[#01193D]">{m.title}</h3>
                <p className="text-[#475569] text-sm leading-relaxed mt-2.5">{m.desc}</p>
                <ul className="grid sm:grid-cols-2 gap-2.5 mt-6 pt-6 border-t border-slate-100">
                  {m.points.map((pt) => (
                    <li key={pt} className="flex items-center gap-2 text-xs font-medium text-[#01193D]">
                      <CheckCircle2 size={14} className="text-[#09B2ED] shrink-0" />
                      {pt}
                    </li>
                  ))}
                </ul>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* WHO IT'S BUILT FOR */}
      <section className="px-5 sm:px-8 py-20 lg:py-24 bg-white" data-testid="school-stakeholders-section">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-3xl">
            <SectionLabel>Campus Stakeholders</SectionLabel>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#01193D]">
              Designed around every role in the school community
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {ROLES.map((role, i) => (
              <Reveal key={role.title} delay={i * 0.1} className="rounded-3xl border border-slate-100 p-8 bg-slate-50/50 flex flex-col justify-between">
                <div>
                  <span className="font-mono text-xs font-semibold uppercase tracking-wider text-[#2563EB]">Role 0{i + 1}</span>
                  <h3 className="font-display font-bold text-xl text-[#01193D] mt-2">{role.title}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed mt-3">{role.desc}</p>
                </div>
                <div className="mt-6 pt-4 border-t border-slate-200/80 font-mono text-xs font-semibold text-emerald-600">
                  ★ {role.metrics}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TECHNICAL SPECIFICATIONS */}
      <section className="px-5 sm:px-8 py-16 bg-[#01193D] text-white" data-testid="school-specs-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TECH_SPECS.map((spec) => (
              <div key={spec.label} className="p-6 rounded-2xl bg-white/5 border border-white/10">
                <spec.icon size={20} className="text-[#09B2ED] mb-3" />
                <div className="text-xs text-slate-400 font-mono">{spec.label}</div>
                <div className="text-sm font-semibold text-white mt-1">{spec.val}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <CTABanner
        title="Modernize your school operations today"
        subtitle="Schedule a full walkthrough of The Aurge Labs - School Management System customized for your institution."
        cta="Book a School Demo"
      />
    </div>
  );
}
